import logger from "@/lib/logger";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Loader2, Save, Building2, Phone, MapPin,
  FileText, Eye, Users, UserPlus, Trash2, HardHat
} from "lucide-react";
import { INDIA_STATES } from "@/lib/constants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SectionCard = ({ icon: Icon, iconBg, iconColor, title, subtitle, children }) => (
  <Card className="rounded-2xl border border-slate-200/70 bg-white">
    <CardContent className="p-5">
      <div className="flex items-center gap-2.5 mb-5">
        <div className={`w-8 h-8 rounded-xl flex items-center justify-center ${iconBg}`}>
          <Icon size={15} className={iconColor} />
        </div>
        <div>
          <h3 className="text-sm font-semibold text-slate-900" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>{title}</h3>
          {subtitle && <p className="text-xs text-slate-400">{subtitle}</p>}
        </div>
      </div>
      {children}
    </CardContent>
  </Card>
);

const DEFAULT_FORM = {
  name: "XSTRONG POWER EV CHARGER",
  tagline: "EV Charger Repair & Service Center",
  phone: "", phone2: "", email: "", website: "",
  address: "", city: "", state: "", pincode: "",
  gst_number: "", working_hours: "Mon-Sat: 9:00 AM - 6:00 PM", about: "",
  overdue_days: 3,
};

export default function Settings() {
  const [loading,  setLoading]  = useState(true);
  const [saving,   setSaving]   = useState(false);
  const [form, setForm] = useState(DEFAULT_FORM);

  // Technicians
  const [technicians, setTechnicians] = useState([]);
  const [newTech,     setNewTech]     = useState("");
  const [savingTechs, setSavingTechs] = useState(false);

  useEffect(() => { fetchSettings(); }, []);

  const fetchSettings = async () => {
    try {
      const [settingsRes, techsRes] = await Promise.all([
        api.get(`${API}/settings`),
        api.get(`${API}/technicians`).catch(() => ({ data: { technicians: [] } })),
      ]);
      const data = settingsRes.data;
      setForm(prev => ({ ...DEFAULT_FORM, ...data }));
      setTechnicians(techsRes.data.technicians || []);
    } catch (err) {
      logger.error("[Settings] fetchSettings failed:", err?.message);
      /* use defaults */
    }
    finally { setLoading(false); }
  };

  const addTechnician = async () => {
    if (!newTech.trim() || technicians.includes(newTech.trim())) return;
    const updated = [...technicians, newTech.trim()];
    setSavingTechs(true);
    try {
      await api.put(`${API}/technicians`, { technicians: updated });
      setTechnicians(updated); setNewTech(""); toast.success("Technician added!");
    } catch { toast.error("Failed to add technician"); }
    finally { setSavingTechs(false); }
  };

  const removeTechnician = async (name) => {
    const updated = technicians.filter(t => t !== name);
    setSavingTechs(true);
    try {
      await api.put(`${API}/technicians`, { technicians: updated });
      setTechnicians(updated); toast.success("Technician removed");
    } catch { toast.error("Failed to remove technician"); }
    finally { setSavingTechs(false); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`${API}/settings`, form);
      toast.success("Settings saved successfully!");
    } catch { toast.error("Failed to save settings"); }
    finally { setSaving(false); }
  };

  const u = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  if (loading) return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 max-w-3xl space-y-5">
      {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-40 rounded-2xl" />)}
    </div>
  );

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 max-w-3xl">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            Service Center Settings
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">Details appear on invoices, job cards & customer tracking portal</p>
        </div>
        <Button className="bg-green-600 hover:bg-green-700 text-white" onClick={handleSave} disabled={saving} data-testid="settings-save-button">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Save Settings
        </Button>
      </div>

      <div className="space-y-5">
        {/* Business Info */}
        <SectionCard icon={Building2} iconBg="bg-green-50" iconColor="text-green-600" title="Business Information">
          <div className="space-y-4">
            <div>
              <Label className="text-xs font-medium text-slate-700">Service Center Name <span className="text-rose-500">*</span></Label>
              <Input value={form.name} onChange={e => u("name", e.target.value)}
                className="mt-1.5 h-9 text-sm font-semibold" data-testid="settings-name-input" />
            </div>
            <div>
              <Label className="text-xs font-medium text-slate-700">Tagline / Description</Label>
              <Input value={form.tagline} onChange={e => u("tagline", e.target.value)}
                className="mt-1.5 h-9 text-sm" placeholder="EV Charger Repair & Service Center" />
            </div>
            <div>
              <Label className="text-xs font-medium text-slate-700">About / Services Offered</Label>
              <Textarea value={form.about} onChange={e => u("about", e.target.value)}
                className="mt-1.5 text-sm resize-none" rows={2}
                placeholder="Specialised in EV charger repair, installation and maintenance..." />
            </div>
          </div>
        </SectionCard>

        {/* Contact */}
        <SectionCard icon={Phone} iconBg="bg-sky-50" iconColor="text-sky-600" title="Contact Details">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs font-medium text-slate-700">Primary Phone <span className="text-rose-500">*</span></Label>
              <Input value={form.phone} onChange={e => u("phone", e.target.value)}
                className="mt-1.5 h-9 text-sm" placeholder="+91 98765 43210" data-testid="settings-phone-input" />
            </div>
            <div>
              <Label className="text-xs font-medium text-slate-700">Secondary Phone</Label>
              <Input value={form.phone2} onChange={e => u("phone2", e.target.value)}
                className="mt-1.5 h-9 text-sm" placeholder="+91 98765 43211" />
            </div>
            <div>
              <Label className="text-xs font-medium text-slate-700">Email Address</Label>
              <Input value={form.email} onChange={e => u("email", e.target.value)}
                className="mt-1.5 h-9 text-sm" placeholder="service@xstrongpower.com" type="email" data-testid="settings-email-input" />
            </div>
            <div>
              <Label className="text-xs font-medium text-slate-700">Website</Label>
              <Input value={form.website} onChange={e => u("website", e.target.value)}
                className="mt-1.5 h-9 text-sm" placeholder="www.xstrongpower.com" />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs font-medium text-slate-700">Working Hours</Label>
              <Input value={form.working_hours} onChange={e => u("working_hours", e.target.value)}
                className="mt-1.5 h-9 text-sm" placeholder="Mon-Sat: 9:00 AM - 6:00 PM" />
            </div>
          </div>
        </SectionCard>

        {/* Address */}
        <SectionCard icon={MapPin} iconBg="bg-amber-50" iconColor="text-amber-600" title="Service Center Address">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <Label className="text-xs font-medium text-slate-700">Street Address</Label>
              <Textarea value={form.address} onChange={e => u("address", e.target.value)}
                className="mt-1.5 text-sm resize-none" rows={2}
                placeholder="Shop No. 12, EV Tech Park, Main Road" data-testid="settings-address-input" />
            </div>
            <div>
              <Label className="text-xs font-medium text-slate-700">City</Label>
              <Input value={form.city} onChange={e => u("city", e.target.value)} className="mt-1.5 h-9 text-sm" placeholder="Mumbai" />
            </div>
            <div>
              <Label className="text-xs font-medium text-slate-700">State</Label>
              <Input value={form.state} onChange={e => u("state", e.target.value)} className="mt-1.5 h-9 text-sm" placeholder="Maharashtra" />
            </div>
            <div>
              <Label className="text-xs font-medium text-slate-700">PIN Code</Label>
              <Input value={form.pincode} onChange={e => u("pincode", e.target.value)}
                className="mt-1.5 h-9 text-sm font-mono" placeholder="400001" maxLength={6} />
            </div>
          </div>
        </SectionCard>

        {/* GST */}
        <SectionCard icon={FileText} iconBg="bg-emerald-50" iconColor="text-emerald-600" title="Business Registration">
          <div>
            <Label className="text-xs font-medium text-slate-700">GST Number</Label>
            <Input value={form.gst_number} onChange={e => u("gst_number", e.target.value)}
              className="mt-1.5 h-9 text-sm font-mono w-64" placeholder="22AAAAA0000A1Z5" maxLength={15} />
            <p className="text-xs text-slate-400 mt-1">Appears on printed invoices</p>
          </div>
        </SectionCard>

        {/* ===== OVERDUE REPAIR THRESHOLD ===== */}
        <Card className="rounded-2xl border border-amber-200/70 bg-amber-50/30">
          <CardContent className="p-5">
            <div className="flex items-center gap-2.5 mb-4">
              <div className="w-8 h-8 rounded-xl bg-amber-100 flex items-center justify-center">
                <span className="text-amber-600 text-base">⚠️</span>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-slate-900" style={{ fontFamily: "'Space Grotesk',sans-serif" }}>
                  Overdue Repair Alert Threshold
                </h3>
                <p className="text-xs text-slate-400">Dashboard warns when a job has been in "Repairing" longer than this</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  min="1"
                  max="30"
                  value={form.overdue_days ?? 3}
                  onChange={e => u("overdue_days", parseInt(e.target.value) || 3)}
                  className="h-9 text-sm font-semibold w-20 text-center"
                  data-testid="settings-overdue-days-input"
                />
                <span className="text-sm text-slate-600 font-medium">days</span>
              </div>
              <p className="text-xs text-slate-500">
                Jobs in <span className="font-semibold text-amber-700">Repairing</span> status for more than{" "}
                <span className="font-bold text-amber-700">{form.overdue_days ?? 3} day{(form.overdue_days ?? 3) !== 1 ? "s" : ""}</span>{" "}
                will show in the overdue alert on the Dashboard and Jobs List.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* ===== TECHNICIANS ===== */}
        <SectionCard icon={HardHat} iconBg="bg-purple-50" iconColor="text-purple-600" title="Technicians" subtitle="Manage your service team — assign them to jobs">
          <div className="space-y-3">
            {technicians.length === 0 && (
              <p className="text-xs text-slate-400 italic">No technicians added yet. Add your team members below.</p>
            )}
            {technicians.map(t => (
              <div key={t} className="flex items-center gap-3 p-2.5 bg-slate-50 rounded-xl border border-slate-100">
                <div className="w-7 h-7 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                  <span className="text-purple-700 text-xs font-bold">{t.charAt(0).toUpperCase()}</span>
                </div>
                <span className="text-sm font-medium text-slate-800 flex-1">{t}</span>
                <button onClick={() => removeTechnician(t)} disabled={savingTechs}
                  className="p-1.5 text-slate-400 hover:text-rose-500 hover:bg-rose-50 rounded-lg transition-colors" title="Remove">
                  <Trash2 size={13}/>
                </button>
              </div>
            ))}
            <div className="flex gap-2 mt-2">
              <Input
                placeholder="Enter technician name (e.g. Rahul, Amit)"
                value={newTech}
                onChange={e => setNewTech(e.target.value)}
                onKeyDown={e => e.key === "Enter" && addTechnician()}
                className="h-9 text-sm flex-1"
                data-testid="settings-new-tech-input"
              />
              <Button size="sm" className="h-9 bg-purple-600 hover:bg-purple-700 text-white px-4"
                onClick={addTechnician} disabled={savingTechs || !newTech.trim()}
                data-testid="settings-add-tech-button">
                {savingTechs ? <Loader2 size={13} className="animate-spin"/> : <UserPlus size={13}/>}
              </Button>
            </div>
          </div>
        </SectionCard>


        {/* Invoice Preview */}
        <Card className="rounded-2xl border border-green-200/70 bg-green-50/40">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-4">
              <Eye size={14} className="text-green-600" />
              <p className="text-xs font-semibold text-green-700 uppercase tracking-wide">Invoice Header Preview</p>
            </div>
            <div className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm">
              <div className="flex justify-between items-start">
                <div className="flex items-center gap-3 flex-1">
                  <img src="/logo.jpg" alt="logo" className="h-12 w-auto object-contain flex-shrink-0" />
                  <div>
                  <p className="font-bold text-sm text-green-700" style={{ fontFamily: "'Space Grotesk',sans-serif" }}>{form.name || "Service Center Name"}</p>
                  <p className="text-xs text-slate-500 mb-1">{form.tagline}</p>
                  <div className="space-y-0.5">
                    {form.phone && <p className="text-xs text-slate-600">&#128222; {form.phone}{form.phone2 ? ` / ${form.phone2}` : ""}</p>}
                    {form.email && <p className="text-xs text-slate-600">&#9993; {form.email}</p>}
                    {form.website && <p className="text-xs text-slate-600">&#127760; {form.website}</p>}
                    {(form.address || form.city) && (
                      <p className="text-xs text-slate-600">
                        &#128205; {[form.address, form.city, form.state, form.pincode].filter(Boolean).join(", ")}
                      </p>
                    )}
                    {form.working_hours && <p className="text-xs text-slate-500">&#128336; {form.working_hours}</p>}
                    {form.gst_number && <p className="text-xs text-slate-400 font-mono">GST: {form.gst_number}</p>}
                  </div>
                  </div>
                </div>
                <div className="text-right ml-4">
                  <p className="font-mono text-lg font-bold text-slate-900">XP-2025-XXXX</p>
                  <p className="text-xs text-slate-400">Job Card</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Bottom save */}
      <div className="flex justify-end mt-6">
        <Button className="bg-green-600 hover:bg-green-700 text-white px-8" onClick={handleSave} disabled={saving}>
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Save All Settings
        </Button>
      </div>
    </div>
  );
}
